import {
  Component,
  Element,
  h,
  Host,
  Prop,
  Watch,
  State,
  Listen
} from '@stencil/core';

import { select } from 'd3-selection';

import {
  CHART_TYPE,
  DATA_TYPE,
  BOXPLOT_LEGEND_DEFAULTS
} from '../../constants';
import { getChartModel } from '../../utils/model-helper';
import { IBoxPlot, BoxPlotChart, IChartData, IChartSeries } from '../../models';

@Component({
  tag: 'uui-box-plot',
  styleUrl: 'uui-box-plot.scss',
  shadow: true
})
export class UuiBoxPlot {
  @Element() element: HTMLElement;

  @Prop() config: IBoxPlot;
  @State() width: number;
  @State() highlightedElement: string = '';
  @State() highlightedElementData: IChartData;
  @State() highlightedBarData: IChartSeries;

  //Todo: Find approprate d3 data types
  public chart: any;
  public barsSvgElement: any;

  model: BoxPlotChart;
  plotAreaWidth: number;
  toolTipElement: HTMLElement;
  hasEmptyMessageSlot: boolean;

  @Listen('resize', { target: 'window' })
  windowResize() {
    if (this.model) {
      this.createChart();
    }
  }

  @Watch('config')
  onConfigChanged(): void {
    this.buildModel();
    requestAnimationFrame(() => {
      this.createChart();
    });
  }

  componentWillLoad(): void {
    this.hasEmptyMessageSlot = !!this.element.querySelector(
      '[slot="empty-message"]'
    );
    this.buildModel();
  }

  componentDidLoad(): void {
    this.createChart();
  }

  buildModel(): void {
    this.model = getChartModel<CHART_TYPE.BOX_PLOT>(this.config as IBoxPlot);
  }

  createChart(): void {
    if (!this.model.series?.length) {
      return;
    }

    this.width = this.model.stretchToMaxWidth
      ? this.element.getBoundingClientRect().width
      : this.model.width;

    const svgElement = select(this.element.shadowRoot.querySelector('svg'))
      .attr('width', this.width)
      .attr('height', this.model.height);

    this.plotAreaWidth = this.model.stretchToMaxWidth
      ? this.width * (this.model.isSmallChart ? 0.95 : 0.85)
      : this.model.plotAreaWidth;

    this.model.plotAreaWidth = this.plotAreaWidth;

    svgElement.selectAll('*').remove();

    this.chart = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    // Y Axis Title
    if (this.model.yAxis.showAxisLabel && !this.model.isSmallChart) {
      this.chart
        .append('text')
        .attr(
          'transform',
          `translate(${this.model.yAxisTitleLeftMargin} ${
            this.model.plotAreaHeight / 2
          }) rotate(-90)`
        )
        .attr('text-anchor', 'middle')
        .text(this.model.yAxis.title)
        .attr('class', 'uui-dv-axis-label');
    }

    // X Axis Title
    this.chart
      .append('text')
      .attr('x', this.plotAreaWidth / 2)
      .attr('y', this.model.plotAreaHeight + 45)
      .attr('text-anchor', 'middle')
      .text(this.model.xAxis.title)
      .attr('class', 'uui-dv-axis-label');

    const yAxisSvg = svgElement
      .append('g')
      .attr('class', 'chart__axis--hidden uui-dv-data-label')
      .attr(
        'transform',
        `translate(${this.model.yAxisSvgLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    const xAxisSvg = svgElement
      .append('g')
      .attr('class', 'chart__axis uui-dv-data-label')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${
          this.model.plotAreaTopMargin + this.model.plotAreaHeight
        })`
      );

    if (!this.model.xAxis.showAxisLine) {
      xAxisSvg.attr('class', 'chart__axis--hidden uui-dv-axis-label');
    }

    yAxisSvg.call(this.model.leftAxis);
    xAxisSvg.call(this.model.bottomAxis);

    if (this.model.isSmallChart && !this.model.isHorizontal) {
      xAxisSvg
        .call(this.model.bottomAxis)
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('transform', `translate(10, 0)`);
    }

    this.barsSvgElement = svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    this.updateChart();
  }

  public buildLine(
    series: IChartSeries,
    line?: IChartData,
    secondline?: IChartData
  ) {
    const lineClass =
      line.type == DATA_TYPE.MEDIAN
        ? 'chart__line chart__line--dashed'
        : 'chart__line';

    this.barsSvgElement
      .append('line')
      .attr('class', lineClass)
      .attr('x1', this.model.xScale(line.y))
      .attr('y1', this.model.getLineYCoord(true, series, secondline))
      .attr('x2', this.model.xScale(secondline ? secondline.y : line.y))
      .attr('y2', this.model.getLineYCoord(false, series, secondline));
  }

  public appendTooltip(
    svgPath: any,
    boxPlotDataPoint: IChartData,
    series: IChartSeries
  ) {
    if (this.model.tooltip) {
      svgPath
        .on('mouseover', (e: MouseEvent) => {
          this.updateTooltipPosition(e.x, e.y);
          this.highlightedElement = boxPlotDataPoint.name;
          this.highlightedBarData = series;
          this.highlightedElementData = boxPlotDataPoint;
          this.toogleTooltip(true);
        })
        .on('mousemove', (e: MouseEvent) => {
          this.updateTooltipPosition(e.x, e.y);
        })
        .on('mouseout', () => {
          this.toogleTooltip(false);
          this.updateTooltipPosition(-1000, -1000); // to displace the tooltip
        });
    }
  }

  //#region - Tooltip
  toogleTooltip(isShow: boolean): void {
    this.toolTipElement.style.opacity = isShow ? '1' : '0';
  }

  updateTooltipPosition(x: number, y: number): void {
    const documentWidth = document.body.offsetWidth;
    const rightSpace = documentWidth - x;
    const tooltipWidth =
      this.toolTipElement.getBoundingClientRect()?.width + 20;

    if (rightSpace <= tooltipWidth) {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.style.left = `${x - tooltipWidth}px`;
    } else {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.style.left = `${x + 20}px`;
    }
    this.toolTipElement.style.top = `${y - 12}px`;
  }

  getChartTooltip(
    highlightedElementData: IChartData,
    highlightedBarData: IChartSeries
  ): JSX.Element {
    const isQuartileRectangle =
      highlightedElementData.type == DATA_TYPE.LOWER_QUARTILE ||
      highlightedElementData.type == DATA_TYPE.UPPER_QUARTILE;

    if (isQuartileRectangle) {
      const lowerQuartile = this.highlightedBarData.data.find(
        d => d.type == DATA_TYPE.LOWER_QUARTILE
      );
      const upperQuartile = this.highlightedBarData.data.find(
        d => d.type == DATA_TYPE.UPPER_QUARTILE
      );

      return (
        <div class="chart__tooltip chart__legend--flex-column">
          {this.getTooltipRow(
            lowerQuartile.name,
            lowerQuartile.y,
            lowerQuartile.typeLabel,
            highlightedBarData.valuePrefix,
            highlightedBarData.valueSuffix
          )}
          {this.getTooltipRow(
            upperQuartile.name,
            upperQuartile.y,
            upperQuartile.typeLabel,
            highlightedBarData.valuePrefix,
            highlightedBarData.valueSuffix
          )}
        </div>
      );
    }

    return this.getTooltipRow(
      highlightedElementData.name,
      highlightedElementData.y,
      highlightedElementData.typeLabel,
      highlightedBarData.valuePrefix,
      highlightedBarData.valueSuffix
    );
  }

  getTooltipRow(name, value, typeLabel, prefix, suffix): JSX.Element {
    typeLabel = typeLabel ? typeLabel : '';

    return (
      <div class="chart__legend--flex-row uui-dv-legend-label chart__tooltip chart__tooltip-row">
        <div>
          {name} {typeLabel && <span>{typeLabel}</span>}
        </div>
        <div class="chart__tooltip--value">{`${': '}${prefix || ''}${value}${
          suffix || ''
        }`}</div>
      </div>
    );
  }
  //#endregion

  public updateChart(): void {
    this.model.series.forEach(series => {
      const bars = this.barsSvgElement.selectAll('.bar').data(series.data);

      bars.exit().remove();

      const lowerDecile = this.model.getBoxPlotDataPoint(
        series,
        DATA_TYPE.LOWER_DECILE
      );
      const upperDecile = this.model.getBoxPlotDataPoint(
        series,
        DATA_TYPE.UPPER_DECILE
      );

      // Lower Limit
      if (lowerDecile) {
        this.buildLine(series, lowerDecile);
      }

      // Upper Limit
      if (upperDecile) {
        this.buildLine(series, upperDecile);
      }

      // Centre Connecting Line
      if (lowerDecile && upperDecile) {
        this.buildLine(series, lowerDecile, upperDecile);
      }

      // Quartile Bar
      const upperQuartile = this.model.getBoxPlotDataPoint(
        series,
        DATA_TYPE.UPPER_QUARTILE
      );
      const lowerQuartile = this.model.getBoxPlotDataPoint(
        series,
        DATA_TYPE.LOWER_QUARTILE
      );

      if (lowerQuartile != null && upperQuartile != null) {
        this.barsSvgElement
          .append('rect')
          .style('fill', this.model.getColorForDataNIndex(lowerQuartile, 0))
          .attr('x', this.model.xScale(lowerQuartile.y))
          .attr(
            'y',
            this.model.getBoxPlotYCoord(series.name) -
              this.model.barHeight * 0.5
          )
          .attr('width', this.model.xScale(upperQuartile.y - lowerQuartile.y))
          .attr('height', this.model.barHeight);
      }

      // Lower Limit
      const median = this.model.getBoxPlotDataPoint(series, DATA_TYPE.MEDIAN);
      if (median) {
        this.buildLine(series, median);
      }

      // Custom Point - Rhombus
      const customPoint = this.model.getBoxPlotDataPoint(
        series,
        DATA_TYPE.CUSTOM_POINT
      );
      if (customPoint) {
        const customPointPath = this.barsSvgElement
          .append('circle')
          .attr('class', 'chart__custom-point')
          .attr('r', this.model.barHeight * 0.65)
          .attr('cx', this.model.xScale(customPoint.y))
          .attr('cy', this.model.getBoxPlotYCoord(series.name));

        this.appendTooltip(customPointPath, customPoint, series);
      }
    });
  }

  getLowerUpperTypeLabel(lowerType: DATA_TYPE, upperType: DATA_TYPE): string {
    const lower = this.model.getBoxPlotDataPoint(
      this.model.series[0],
      lowerType
    );

    const upper = this.model.getBoxPlotDataPoint(
      this.model.series[0],
      upperType
    );

    var lowerTypeLabel = lower ? (lower.typeLabel ? lower.typeLabel : '') : '';

    var upperTypeLabel = upper ? (upper.typeLabel ? upper.typeLabel : '') : '';

    return `${lowerTypeLabel}${
      lowerTypeLabel && upperTypeLabel ? '/' : ''
    }${upperTypeLabel}`;
  }

  getChartLegendRowElement(element: string): JSX.Element {
    var typeLabel = this.getTypeLabel(element);

    return (
      <div class="chart__legend-row">
        <div class="chart__legend--flex-row chart__legend-row-item">
          <div>{this.getLegendItemSvg(element)}</div>
          <div class="chart__legend--flex-row uui-dv-legend-label chart__legend-text">
            <div>{`${element} ${typeLabel}`}</div>
          </div>
        </div>
      </div>
    );
  }

  getTypeLabel(element: string): string {
    if (element == BOXPLOT_LEGEND_DEFAULTS.decile) {
      return this.getLowerUpperTypeLabel(
        DATA_TYPE.LOWER_DECILE,
        DATA_TYPE.UPPER_DECILE
      );
    }
    if (element == BOXPLOT_LEGEND_DEFAULTS.quartile) {
      return this.getLowerUpperTypeLabel(
        DATA_TYPE.LOWER_QUARTILE,
        DATA_TYPE.UPPER_QUARTILE
      );
    }
    if (element == BOXPLOT_LEGEND_DEFAULTS.median) {
      const median = this.model.getBoxPlotDataPoint(
        this.model.series[0],
        DATA_TYPE.MEDIAN
      );

      return median ? (median.typeLabel ? median.typeLabel : '') : '';
    }
    const customPoint = this.model.getBoxPlotDataPoint(
      this.model.series[0],
      DATA_TYPE.CUSTOM_POINT
    );

    return customPoint
      ? customPoint.typeLabel
        ? customPoint.typeLabel
        : ''
      : '';
  }

  getLegendItemSvg(element: string): JSX.Element {
    const lowerQuartile = this.model.getBoxPlotDataPoint(
      this.model.series[0],
      DATA_TYPE.LOWER_QUARTILE
    );

    return (
      <div class="chart__legend-svg">
        {(element == BOXPLOT_LEGEND_DEFAULTS.decile ||
          element == BOXPLOT_LEGEND_DEFAULTS.median) && (
          <svg width="1px" height="14px">
            {this.getLegendSvgLine(element)}
          </svg>
        )}

        {element == BOXPLOT_LEGEND_DEFAULTS.quartile && (
          <svg width="14px" height="14px">
            <circle
              cx="7"
              cy="7"
              r="7"
              fill={this.model.getColorForDataNIndex(lowerQuartile, 0)}
            />
          </svg>
        )}

        {this.model.customPointName != null && element == this.model.customPointName && (
          <svg width="14px" height="14px">
            <circle cx="7" cy="7" r="7" class="chart__custom-point" />
          </svg>
        )}
      </div>
    );
  }

  getLegendSvgLine(element: string): JSX.Element {
    const className =
      element == BOXPLOT_LEGEND_DEFAULTS.decile
        ? 'chart__line'
        : 'chart__line chart__line--dashed';
    return <line x1="0" x2="0" y1="0" y2="14" class={className}></line>;
  }

  // TODO: this hardcodes the first series, but it looks like this chart
  // only supports one series anyway, in the future this should be
  // tidied up.
  getChartLegend(): JSX.Element {
    const hasMedian = this.model.series[0].data.find(item => item.type === DATA_TYPE.MEDIAN)
    let medianLegend = null
    if (hasMedian) {
      medianLegend = this.getChartLegendRowElement(BOXPLOT_LEGEND_DEFAULTS.median)
    }

    const hasCustomPoint = this.model.series[0].data.find(item => item.type === DATA_TYPE.CUSTOM_POINT)
    let customPointLegend = null
    if (hasCustomPoint) {
      customPointLegend = this.getChartLegendRowElement(this.model.customPointName)
    }

    const hasQuartile = this.model.series[0].data.find(item => item.type === DATA_TYPE.LOWER_QUARTILE) != null && this.model.series[0].data.find(item => item.type === DATA_TYPE.UPPER_QUARTILE) != null;
    let quartileLegend = null
    if (hasQuartile) {
      quartileLegend = this.getChartLegendRowElement(BOXPLOT_LEGEND_DEFAULTS.quartile)
    }

    const hasDecile = this.model.series[0].data.find(item => item.type === DATA_TYPE.LOWER_DECILE) != null && this.model.series[0].data.find(item => item.type === DATA_TYPE.UPPER_DECILE) != null;
    let decileLegend = null
    if (hasDecile) {
      decileLegend = this.getChartLegendRowElement(BOXPLOT_LEGEND_DEFAULTS.decile)
    }

    return (
      <div class="chart__legend--flex-column chart__legend--flex-centre">
        <div class="chart__legend--flex-row">
          {customPointLegend}
          {medianLegend}
        </div>
        {decileLegend}
        {quartileLegend}
      </div>
    );
  }

  getChartTitle(): JSX.Element {
    return (
      (this.model.title || this.model.subTitle) && (
        <uui-chart-title model={this.model}>
          {this.model.title?.useSlot && <slot name="title" slot="title"></slot>}
          {this.model.subTitle?.useSlot && (
            <slot name="sub-title" slot="sub-title"></slot>
          )}
        </uui-chart-title>
      )
    );
  }

  render() {
    const chartSectionClass = this.model.isShowChart
      ? 'chart__wrapper'
      : 'chart--hidden';
    const viewBoxCoords =
      this.model.stretchToMaxWidth && this.width
        ? `0 0 ${this.width} ${this.model.height}`
        : '';

    if (!this.model.isShowChart) {
      return (
        <div>
          {this.getChartTitle()}
          <uui-chart-empty-message
            model={this.model}
            hasSlot={this.hasEmptyMessageSlot}
          >
            <slot name="empty-message" slot="empty-message"></slot>
          </uui-chart-empty-message>
        </div>
      );
    }

    return (
      <Host>
        {this.getChartTitle()}

        <section class={chartSectionClass}>
          {/* ------- chart ---------- */}
          <svg
            class="chart__svg"
            {...(this.model.stretchToMaxWidth &&
              this.width && { viewBox: viewBoxCoords })}
            preserveAspectRatio="none"
          ></svg>

          {/* --------- legend ------------- */}
          {this.model.legend && this.getChartLegend()}
        </section>

        {/* ------------ Tooltip -------------- */}
        {this.model.tooltip && (
          <div
            class="chart__tooltip chart__tooltip--container chart__tooltip-container-pointer-left"
            ref={el => (this.toolTipElement = el)}
          >
            {this.highlightedElement != '' &&
              this.getChartTooltip(
                this.highlightedElementData,
                this.highlightedBarData
              )}
          </div>
        )}
      </Host>
    );
  }
}
